import axios from 'axios';
import { format, parseISO, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { HorarioDisponible, Fisioterapeuta, Cita, Paciente } from '../types';

// URL base para el servidor backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// URL forzada para producción en Render
const isProduction = window.location.hostname.includes('onrender.com');
const RENDER_API_URL = 'https://fisiofine.onrender.com/api';

// Usar URL forzada en producción
const FINAL_API_URL = isProduction ? RENDER_API_URL : API_URL;

// Log para depuración
console.log('Configuración de API:');
console.log('- URL de entorno:', API_URL);
console.log('- URL forzada:', RENDER_API_URL);
console.log('- URL final usada:', FINAL_API_URL);

/**
 * Obtiene los fisioterapeutas disponibles
 */
export const obtenerFisioterapeutas = async (): Promise<Fisioterapeuta[]> => {
  try {
    console.log(`Solicitando fisioterapeutas a: ${FINAL_API_URL}/fisioterapeutas`);
    const response = await axios.get(`${FINAL_API_URL}/fisioterapeutas`);
    
    // Verificar la estructura de la respuesta
    console.log('Respuesta recibida del servidor:', response.data);
    
    // Extraer el array de fisioterapeutas de la estructura de respuesta
    if (response.data && response.data.fisioterapeutas && Array.isArray(response.data.fisioterapeutas)) {
      console.log('Array de fisioterapeutas encontrado, longitud:', response.data.fisioterapeutas.length);
      return response.data.fisioterapeutas;
    } else {
      console.error('La respuesta no tiene la estructura esperada:', response.data);
      // Si la respuesta ya es un array (aunque no debería), intentar usarla
      if (Array.isArray(response.data)) {
        return response.data;
      }
      // En caso contrario, devolver array vacío para evitar errores
      return [];
    }
  } catch (error) {
    console.error('Error al obtener fisioterapeutas:', error);
    // Devolver array vacío para evitar errores en la UI
    return [];
  }
};

/**
 * Obtiene los horarios disponibles para un fisioterapeuta en una semana específica
 */
export const obtenerHorariosDisponibles = async (
  fisioterapeutaId: string, 
  fechaInicio: Date
): Promise<HorarioDisponible[]> => {
  try {
    // Formatear la fecha de inicio y fin para la API
    const fechaInicioStr = format(fechaInicio, 'yyyy-MM-dd');
    const fechaFin = addDays(fechaInicio, 6); // Una semana completa
    const fechaFinStr = format(fechaFin, 'yyyy-MM-dd');
    
    console.log(`Solicitando disponibilidad a: ${FINAL_API_URL}/disponibilidad para fisio ${fisioterapeutaId} del ${fechaInicioStr} al ${fechaFinStr}`);
    const response = await axios.get(`${FINAL_API_URL}/disponibilidad`, {
      params: {
        fisioterapeutaId,
        fechaInicio: fechaInicioStr,
        fechaFin: fechaFinStr
      }
    });
    
    // Verificar estructura de respuesta
    console.log('Respuesta de disponibilidad:', response.data);
    
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && Array.isArray(response.data.disponibilidad)) {
      return response.data.disponibilidad;
    } else {
      console.error('Formato de respuesta inesperado para disponibilidad:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error al obtener horarios disponibles:', error);
    return [];
  }
};

/**
 * Crea una nueva cita en el calendario
 */
export const crearCita = async (
  cita: Omit<Cita, 'id'> & { paciente?: Paciente }
): Promise<Cita | null> => {
  try {
    console.log(`Creando cita en: ${FINAL_API_URL}/citas`);
    console.log('Datos enviados al crear cita:', cita);
    const response = await axios.post(`${FINAL_API_URL}/citas`, cita);
    console.log('Respuesta al crear cita:', response.data);
    
    if (response.data && response.data.exito && response.data.cita) {
      return response.data.cita;
    } else if (response.data && typeof response.data === 'object') {
      return response.data;
    } else {
      console.error('Formato de respuesta inesperado al crear cita:', response.data);
      return null;
    }
  } catch (error) {
    console.error('Error al crear la cita:', error);
    return null;
  }
};

/**
 * Confirma una cita después del pago exitoso
 */
export const confirmarCita = async (
  citaId: string, 
  sesionStripeId: string
): Promise<Cita | null> => {
  try {
    console.log(`Confirmando cita ${citaId} con sesión Stripe ${sesionStripeId}`);
    const response = await axios.patch(`${FINAL_API_URL}/citas/${citaId}/confirmar`, {
      sesionStripeId
    });
    console.log('Respuesta al confirmar cita:', response.data);
    
    if (response.data && response.data.exito && response.data.cita) {
      return response.data.cita;
    } else if (response.data && typeof response.data === 'object') {
      return response.data;
    } else {
      console.error('Formato de respuesta inesperado al confirmar cita:', response.data);
      return null;
    }
  } catch (error) {
    console.error('Error al confirmar la cita:', error);
    return null;
  }
};

/**
 * Elimina una cita cuando el pago es cancelado o no se completa
 */
export const eliminarCita = async (citaId: string): Promise<boolean> => {
  try {
    console.log(`Eliminando cita cancelada con ID: ${citaId}`);
    const response = await axios.delete(`${FINAL_API_URL}/citas/${citaId}`);
    console.log('Respuesta al eliminar cita:', response.data);
    
    if (response.data && response.data.exito) {
      return true;
    } else {
      console.error('Formato de respuesta inesperado al eliminar cita:', response.data);
      return false;
    }
  } catch (error) {
    console.error('Error al eliminar la cita:', error);
    return false;
  }
}; 